import { useEffect, useState } from "react";
import { supabase } from "./supabaseClient";
import ProposalPage from "./ProposalPage";

/**
 * The customer's proposal, by exact id.
 *
 * 1. get_proposal_for_review(p_id) — a database function that returns one
 *    proposal by exact id and nothing else. It is the only path that keeps
 *    working once signed-out read access to the proposals table is removed
 *    (supabase/pending/…proposals_rls_lockdown…).
 * 2. If that call fails for ANY reason — above all because the function is not
 *    in production yet — fall back to today's direct read of the table.
 *
 * So every /review link loads before the lockdown (function or not) and after
 * it (function). Returns the proposal's `data` object, or null when no
 * proposal has that id.
 */
export async function loadProposalForReview(
  id: string
): Promise<{ data: any; error: { message?: string } | null; via: "rpc" | "table" }> {
  const rpc = await supabase.rpc("get_proposal_for_review", { p_id: id });
  if (!rpc.error) {
    return { data: rpc.data ?? null, error: null, via: "rpc" };
  }
  const direct = await supabase
    .from("proposals")
    .select("data")
    .eq("id", id)
    .maybeSingle();
  if (direct.error) {
    return { data: null, error: direct.error, via: "table" };
  }
  return { data: direct.data?.data ?? null, error: null, via: "table" };
}

/**
 * Tell the server the customer opened this proposal (record-proposal-view),
 * which lights the app's "Clicked" badge now that the email links straight
 * here instead of through the track-click redirect.
 *
 * Fire-and-forget: called only after the proposal has loaded, never awaited
 * by the page, and every failure (offline, blocked, function not deployed)
 * is swallowed — the customer never sees an error from it.
 */
export function recordProposalView(id: string): void {
  try {
    Promise.resolve(
      supabase.functions.invoke("record-proposal-view", { body: { proposalId: id } })
    ).catch(() => {});
  } catch {
    // never surface to the customer
  }
}

export default function ReviewProposalPage() {
  const raw = window.location.pathname.split("/review/")[1] || "";
  const id = decodeURIComponent(raw).split(/[?#]/)[0].replace(/\/+$/, "").trim();

  const [proposalData, setProposalData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        if (!id) {
          setLoadError("Missing proposal id in URL.");
          setProposalData(null);
          setLoading(false);
          return;
        }
        const { data: proposalRow, error } = await loadProposalForReview(id);

        if (!alive) return;

        if (error) {
          console.error("Failed to load proposal:", { id, error });
          setLoadError(error.message || "Failed to load proposal.");
          setProposalData(null);
          setLoading(false);
          return;
        }
        if (!proposalRow) {
          setLoadError(null);
          setProposalData(null);
          setLoading(false);
          return;
        }

        const proposal = proposalRow || {};
const orgId = proposal?.orgId ?? null;

        // Settings come ONLY from the copy frozen into the proposal at send
        // time (proposals.data.userSettings) — never a live lookup. That copy
        // carries the org's proposal wording (labels, headings, Next steps,
        // validity days) as it was when this proposal went out, so renaming a
        // label later cannot change what an existing customer link says.
        // (The live lookup that used to be here read raw user_settings rows,
        // a different shape from what ProposalPage reads, so it could only
        // ever have blanked the sender's details.)

        // ✅ IMPORTANT:
        // Public review should render from the snapshot stored inside proposals.data.
        // So we DO NOT fetch proposal_sections here (RLS will often block it).
        setProposalData({
          ...proposal,
          proposalId: id,
          orgId,
          userSettings: proposal?.userSettings ?? null,
          proposalSectionsSnapshot: proposal?.proposalSectionsSnapshot ?? null,
          proposalLayoutOrder: proposal?.proposalLayoutOrder ?? null,
          proposalNotesSnapshot: proposal?.proposalNotesSnapshot ?? null,
          // SOW now lives directly on the snapshot as sowText. Fall back to
          // the legacy sowCustomTextSnapshot for proposals saved before that
          // migration so they still render their customized scope.
          sowText:
            typeof proposal?.sowText === "string"
              ? proposal.sowText
              : proposal?.sowModeSnapshot === "custom"
                ? String(proposal?.sowCustomTextSnapshot || "")
                : "",
          // Project Description lives on the snapshot (so it's already in the
          // ...proposal spread). When empty, ProposalPage regenerates it from
          // constructionType + deckingQty, both of which are also in the row.
          projectDescription:
            typeof proposal?.projectDescription === "string"
              ? proposal.projectDescription
              : "",
          // Carry the touched flags so an intentionally-empty or custom SOW /
          // Project Description renders exactly as the admin left it, instead of
          // the read-only view regenerating the auto template. (Older proposals
          // lack these → false → legacy non-empty text still shows via `||`.)
          sowTouched: !!proposal?.sowTouched,
          projectDescriptionTouched: !!proposal?.projectDescriptionTouched,
          startWeeksSnapshot: proposal?.startWeeksSnapshot ?? null,
          durationDaysSnapshot: proposal?.durationDaysSnapshot ?? null,
          startDateSnapshot: proposal?.startDateSnapshot ?? null,
          showLineItemPricesSnapshot: proposal?.showLineItemPricesSnapshot ?? null,
          showVisualAppendixSnapshot: proposal?.showVisualAppendixSnapshot ?? null,
        });

        setLoadError(null);
        setLoading(false);
        recordProposalView(id);
      } catch (e: any) {
        console.error("ReviewProposalPage fatal error:", e);
        setLoadError(e?.message || "Unexpected error.");
        setProposalData(null);
        setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [id]);

  if (loading) return <div style={{ padding: 40 }}>Loading proposal…</div>;

  if (!proposalData) {
    return (
      <div style={{ padding: 40 }}>
        <div>Proposal not found.</div>
        {loadError ? (
          <pre style={{ marginTop: 12, padding: 12, background: "#f6f6f6" }}>
            {loadError}
          </pre>
        ) : null}
      </div>
    );
  }

  return <ProposalPage {...proposalData} readOnly />;
}
